import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ToiletWarriorActivation.css';

/**
 * TOILET WARRIOR ACTIVATION MODAL
 * 
 * Shows epic celebration when user activates Toilet Mode
 * Auto-dismisses after 3 seconds or on button click
 */

function ToiletWarriorActivation({ isOpen, onClose }) {
  // Auto-dismiss after 3 seconds
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="toilet-warrior-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="toilet-warrior-modal"
            initial={{ opacity: 0, scale: 0.75, y: 60 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 40 }}
            transition={{ 
              type: "spring", 
              damping: 18, 
              stiffness: 260,
              mass: 0.8,
            }}
          >
            {/* Warrior Image */}
            <motion.div 
              className="warrior-image-container"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ 
                delay: 0.2,
                type: "spring",
                damping: 15,
                stiffness: 200
              }}
            >
              <motion.img
                src="/toiletwarrior.png"
                alt="Toilet Warrior"
                className="warrior-image"
                animate={{ 
                  filter: [
                    'drop-shadow(0 0 20px rgba(255, 215, 0, 0.6))',
                    'drop-shadow(0 0 40px rgba(255, 215, 0, 0.8))',
                    'drop-shadow(0 0 20px rgba(255, 215, 0, 0.6))'
                  ]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              />
            </motion.div>

            {/* Title */}
            <motion.h2
              className="warrior-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              🚽 TOILET WARRIOR MODE! 🚽
            </motion.h2>

            {/* Subtitle */}
            <motion.p
              className="warrior-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Premium Features Unlocked!
            </motion.p>

            {/* Benefits List */}
            <motion.div
              className="warrior-benefits"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {[
                { icon: '⚡', text: '+15% Bonus Rewards' },
                { icon: '⛽', text: '$DISTIL Gas Rebate (+2 per purge)' },
                { icon: '🎰', text: 'Exclusive Mini-Games' },
              ].map((benefit, i) => (
                <motion.div
                  key={i}
                  className="benefit-item"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.55 + i * 0.1, type: 'spring', stiffness: 300, damping: 22 }}
                >
                  <span className="benefit-icon">{benefit.icon}</span>
                  <span className="benefit-text">{benefit.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Button */}
            <motion.button
              className="warrior-cta-button"
              onClick={onClose}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              LFG! 🔥
            </motion.button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default ToiletWarriorActivation;
